import { useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { CompetenceDto, UserDto } from 'utils/wemble-api.generated';
import { usePrefetch, useUpdateSkillMutation } from 'utils/wemble-api';
import SkillLevelTrack from '../SkillLevelTrack';
import { ALL_CATEGORIES, categorySections } from './competenceTree';

const useStyles = makeStyles((theme) => ({
  // Groups are told apart by space: a wide gap above each group, tight tiles
  // within it. The first matches the gap the card's tables leave below a
  // section header. Same rhythm and heading as the Grid view.
  section: {
    marginTop: 28,
    '&:first-child': {
      marginTop: 16,
    },
  },
  // Group headings must not read as one more competence: bold tracked caps in
  // the dark accent, outdented from the rows below, with a rule running to the
  // edge that marks where the group starts.
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    color: '#38566B',
    whiteSpace: 'nowrap' as const,
    '&::after': {
      content: '""',
      flex: 1,
      height: 2,
      borderRadius: 1,
      backgroundColor: theme.palette.gray.g6,
    },
    marginBottom: 8,
  },
  grid: {
    display: 'grid',
    // min() so a phone-width card never forces a 260px column wider than itself.
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))',
    gap: 8,
  },
  cell: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '4px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 40,
    cursor: 'pointer',
    // Touch conventions used across the app's tappable controls: no grey iOS
    // tap flash, no text selection on long-press.
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none' as const,
  },
  name: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
  },
  empty: {
    fontSize: 12,
    color: theme.palette.gray.g2,
    marginTop: 12,
  },
}));

/** The person's competencies as they stand today — only what they hold,
 *  narrowed to one category at a time. Levels are editable here and in the
 *  Grid, under the same canEdit rule. */
const CompetenceOverview = ({
  user,
  orderUser,
  competences,
  category,
  canEdit,
}: {
  user: UserDto;
  /** Snapshot used only to order the groups (see UserCompetencesTable). */
  orderUser?: UserDto;
  competences: CompetenceDto[];
  category: string;
  canEdit: boolean;
}) => {
  const classes = useStyles();
  const history = useHistory();
  const prefetchCompetence = usePrefetch('getCompetence');
  const [updateSkill] = useUpdateSkillMutation();

  // Competence id -> the person's skill entry, so a cell can edit the level in
  // place the way the old table row did.
  const skills = useMemo(() => {
    const map = new Map<string, { skillId: string; level: number; competence: CompetenceDto }>();
    for (const skill of user.skills ?? []) {
      if (typeof skill === 'string') continue;
      const competence = typeof skill.competence === 'string' ? undefined : skill.competence;
      const competenceId = typeof skill.competence === 'string' ? skill.competence : competence?._id;
      if (!competenceId || !skill._id) continue;
      const level = skill.level;
      map.set(competenceId, {
        skillId: skill._id,
        level: typeof level === 'string' || !level ? -1 : level.level ?? -1,
        competence: competence ?? { _id: competenceId },
      });
    }
    return map;
  }, [user.skills]);

  const sections = useMemo(() => {
    const grouped = categorySections(competences, category, orderUser ?? user)
      .map((section) => ({
        ...section,
        competences: section.competences.filter((c) => c._id && skills.has(c._id)),
      }))
      .filter((section) => section.competences.length > 0);

    // Showing everything means everything: a competence the person holds that
    // the category tree can't place (no parent resolved, category deleted)
    // still has to appear, or their profile would silently drop it.
    if (category !== ALL_CATEGORIES) return grouped;
    const placed = new Set(grouped.flatMap((section) => section.competences.map((c) => c._id)));
    const unplaced = Array.from(skills.values())
      .filter((skill) => !placed.has(skill.competence._id))
      .map((skill) => skill.competence);
    if (unplaced.length > 0) {
      grouped.push({ label: grouped.length > 0 ? 'Other' : null, competences: unplaced });
    }
    return grouped;
  }, [competences, category, skills, user, orderUser]);

  if (sections.length === 0) {
    return (
      <div className={classes.empty}>
        {category === ALL_CATEGORIES ? 'No competencies registered yet.' : 'No competencies registered in this category.'}
      </div>
    );
  }

  return (
    <div>
      {sections.map((section, index) => (
        <div key={`${index}-${section.label ?? ''}`} className={classes.section}>
          {section.label && <div className={classes.sectionLabel}>{section.label}</div>}
          <div className={classes.grid}>
            {section.competences.map((competence) => {
              const skill = competence._id ? skills.get(competence._id) : undefined;
              return (
                <div
                  key={competence._id}
                  className={classes.cell}
                  onClick={() => history.push(`/competences/${competence._id}`)}
                  onMouseEnter={() => competence._id && prefetchCompetence({ id: competence._id }, { ifOlderThan: 180 })}
                >
                  <div className={classes.name}>{competence.name}</div>
                  <SkillLevelTrack
                    size="inline"
                    level={skill?.level}
                    editable={canEdit && !!skill}
                    onChange={(level) =>
                      skill && updateSkill({ id: skill.skillId, skillUpdateParams: { levelOfSkillId: level?._id ?? undefined } })
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompetenceOverview;
